import { isMultiTenant } from "@/Configs/config";
import { useAuth0 } from "@/Contexts";
import ArrowDown from "@/Icons/ArrowDown";
import { DropdownArrowIcon } from "@/Icons/DropdownArrow";
import User from "@/Icons/User";
import { useTerminalsState, useTerminalsStateRE } from "@/States/Terminals";
import { isCarrotAdmin } from "@/Utils/authUtils";
import resetOnSwitchClient from "@/Utils/resetOnSwitchClient";
import useClickoutside from "@/Utils/useClickoutside";
import { useNavigate } from "@/Utils/useNavigate";
import { useConfig } from "@/api/hooks/useConfig";
import { useSelectedSite } from "@/api/hooks/useSelectedSite";
import { useSites } from "@/api/hooks/useSites";
import { useMultiSelectContext } from "@/components/pageCards/multiListTable/multiSelectContext";
import { useTrans } from "@/translations";

import { useRef, useState } from "react";
import { Link } from "react-router-dom";

import { orderBy } from "lodash";
import { twMerge } from "tailwind-merge";

import { HeaderLogo } from "./HeaderLogo";
import { LanguageSelection } from "./LanguageSelection";

export const UserButton = () => {
  const [isOpen, setOpen] = useState<boolean>(false);
  const { user, logout } = useAuth0()!;
  const { t } = useTrans();
  const containerWrapper = useRef(null);
  const { isMWM } = useConfig();
  const { currentTerminal } = useTerminalsState();
  const { selectedSite } = useSelectedSite();

  const { sites } = useSites({ enabled: isMultiTenant });

  const displayName = selectedSite?.displayName ?? "";

  useClickoutside(containerWrapper, () => setOpen(false));

  return (
    <div className="relative block text-left" ref={containerWrapper}>
      <div className="flex">
        <button
          type="button"
          className="inline-flex items-center gap-3 bg-white p-0 hover:bg-white focus:outline-hidden active:bg-white"
          id="menu-button"
          aria-expanded={isOpen}
          aria-haspopup="true"
          onClick={() => setOpen((prevState) => !prevState)}
        >
          {!isMWM ? (
            <>
              <div className="text-right">
                {displayName && (
                  <div className="text-sm leading-tight font-semibold">
                    {displayName}
                  </div>
                )}
                {currentTerminal?.name && (
                  <div className="text-xs leading-tight text-neutral-500">
                    {currentTerminal.name}
                  </div>
                )}
              </div>
              <HeaderLogo
                displayName={displayName || currentTerminal?.name || "?"}
              />
              <DropdownArrowIcon
                className={twMerge(
                  "transition-transform duration-200",
                  isOpen && "rotate-180",
                )}
              />
            </>
          ) : (
            <User />
          )}
        </button>
      </div>
      <div
        className={twMerge(
          "ring-grey1 visible absolute right-0 z-10 mt-2 w-56 origin-top-right bg-white ring-1 focus:outline-hidden",
          !isOpen && "invisible",
        )}
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="menu-button"
        tabIndex={-1}
      >
        <div role="none" className="divide-grey2 divide-y">
          <div
            className="text-grey4 block px-4 py-3.5 text-sm"
            role="menuitem"
            tabIndex={-1}
            id="menu-item-0"
          >
            <span className="text-grey4 text-sm">{user.email}</span>
          </div>
          {!isMWM && <TerminalSelection />}
          <LanguageSelection />
          {isCarrotAdmin() && (
            <Link
              className="block w-full bg-white px-4 py-3.5 text-left text-sm font-normal hover:bg-neutral-100 active:bg-neutral-100"
              to={`/global-settings`}
              onClick={() => setOpen(false)}
            >
              {t("configLabels:globalSettings")}
            </Link>
          )}
          {!isMWM && (
            <Link
              className="block w-full bg-white px-4 py-3.5 text-left text-sm font-normal hover:bg-neutral-100 active:bg-neutral-100"
              to={`/settings`}
              onClick={() => setOpen(false)}
            >
              {t("configLabels:settings")}
            </Link>
          )}
          {isMultiTenant && (sites?.length ?? 1) > 1 && (
            <div>
              <button
                className="text-carrot-orange block w-full bg-white px-4 py-3.5 text-left text-sm font-normal hover:bg-white active:bg-white"
                role="menuitem"
                tabIndex={-1}
                id="menu-item-1"
                onClick={resetOnSwitchClient}
              >
                {t("actions:switchClient")}
              </button>
            </div>
          )}
          <div>
            <button
              className="text-carrot-red block w-full bg-white px-4 py-3.5 text-left text-sm font-normal hover:bg-white active:bg-white"
              role="menuitem"
              tabIndex={-1}
              id="menu-item-1"
              onClick={() =>
                logout({ logoutParams: { returnTo: window.location.origin } })
              }
            >
              {t("actions:logout")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserButton;

const TerminalSelection = () => {
  const [active, setActive] = useState(false);
  const { terminals, currentTerminal, setTerminalState } =
    useTerminalsStateRE();
  const { t } = useTrans();
  const navigate = useNavigate();
  const { setSelectedIds } = useMultiSelectContext();

  const containerWrapper = useRef(null);
  useClickoutside(containerWrapper, () => setActive(false));

  const onChangeTerminal = (terminal: (typeof terminals)[number]) => {
    setTerminalState({
      currentTerminal: terminal,
    });
    setActive(false);
    setSelectedIds([]);

    // explicitly rerout on manage infrastructure
    if (window.location.pathname.includes("infrastructure/manage"))
      navigate({
        pathname: "/infrastructure/manage",
      });
    // remove search params when changing real estate

    // explicitly rerout on manage customers
    if (window.location.pathname.includes("customer/manage"))
      navigate({
        pathname: "/customer/manage",
      });
    // remove search params when changing real estate
    navigate({ pathname: window.location.pathname });
  };

  const sortedRealEstates = orderBy(terminals, "name");

  if (sortedRealEstates.length === 1) return null;

  return (
    <div className="relative">
      <div
        className="flex cursor-pointer items-center justify-between px-4 py-2.5 text-sm"
        onClick={() => {
          setActive((_active) => !_active);
        }}
      >
        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
          {currentTerminal.name}
        </div>
        <div>
          <ArrowDown className="-rotate-90" />
        </div>
      </div>
      {active && (
        <div className="relative w-0" ref={containerWrapper}>
          <div className="absolute -top-10 right-0 z-20 w-64 border bg-white px-3 py-6 pb-3">
            <div className="flex cursor-pointer justify-between gap-2">
              <div className="flex gap-2">
                <div className="font-bold whitespace-nowrap">
                  {t("actions:selectBuilding")}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-0.5 pt-6">
              <div className="max-h-96 divide-y overflow-auto text-sm leading-tight">
                {sortedRealEstates.map((realEstate, index) => {
                  const isSelected = realEstate.id === currentTerminal.id;
                  return (
                    <div
                      className={twMerge(
                        "flex cursor-pointer justify-between p-3 select-none",
                        "hover:bg-grey2",
                        isSelected && "bg-grey3",
                      )}
                      key={`terminalOption_${index}`}
                      onClick={() => onChangeTerminal(realEstate)}
                    >
                      {realEstate.name}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
